import React, {createContext, useContext, useState} from 'react';
import _ from 'lodash';
import {NodeTypes} from '../constants/nodeTypes';
import {convertCircuitToTree, convertTreeToCircuit} from '../components/treeView/utils/treeUtils';
import {useJsonEditor} from './JsonEditorContext';

const TreeContext = createContext();

const initialTree = {
  name: '0',
  type: NodeTypes.ADDNODE,
};

export const TreeProvider = ({children}) => {
  const {currentJsonText, setCurrentJsonText, updateCurrentJsonText: rawUpdateJsonText} = useJsonEditor();
  const [treeData, setTreeData] = useState(initialTree);
  const [selectedNode, setSelectedNode] = useState('0');
  const [editNodeModalOpen, setEditNodeModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  /** Sync JSON text → tree data. Called when the user types in the JSON editor. */
  const updateCurrentJsonText = (newJsonText) => {
    rawUpdateJsonText(newJsonText);
    let newJson = {};
    try {
      newJson = JSON.parse(newJsonText);
    } catch {
      newJson = {err: 'invalid JSON'};
    }
    if (newJson.circuits?.length > 0) {
      setTreeData(convertCircuitToTree(newJson.circuits[0], '0'));
    } else {
      setTreeData(initialTree);
    }
  };

  /** Sync tree data → JSON text. Called after tree mutations. */
  const updateTreeData = (newTreeData) => {
    setTreeData(newTreeData);
    const newJson = {
      ...JSON.parse(currentJsonText),
      circuits: [],
    };
    const convertedCircuit = convertTreeToCircuit(newTreeData);
    if (Object.keys(convertedCircuit).length > 0) {
      newJson.circuits.push(convertedCircuit);
    }
    setCurrentJsonText(JSON.stringify(newJson, null, 2));
  };

  const deleteNode = (treeDataArg, nodeId) => {
    const findAndDeleteNode = (node, id, parent = null) => {
      if (node.name === id) {
        if (parent) {
          parent.children = parent.children.filter((child) => child.name !== id);
        } else {
          node.children = [];
          node.attributes = {};
          node.type = NodeTypes.ADDNODE;
        }
        return true;
      }
      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          if (findAndDeleteNode(node.children[i], id, node)) return true;
        }
      }
      return false;
    };
    const newTreeData = _.cloneDeep(treeDataArg);
    findAndDeleteNode(newTreeData, nodeId);
    updateTreeData(newTreeData);
  };

  const findNodeByName = (node, name) => {
    if (node.name === name) return node;
    if (node.children && node.children.length) {
      for (const child of node.children) {
        const result = findNodeByName(child, name);
        if (result) return result;
      }
    }
    return null;
  };

  const getNewChild = () => {
    const parts = selectedNode.split(',');
    parts[parts.length - 1] = (parseInt(parts[parts.length - 1]) + 1).toString();
    return {name: `${parts.join(',')}`, type: NodeTypes.ADDNODE};
  };

  return (
    <TreeContext.Provider value={{
      treeData,
      updateTreeData,
      updateCurrentJsonText,
      selectedNode,
      setSelectedNode,
      editNodeModalOpen,
      setEditNodeModalOpen,
      isEditMode,
      setIsEditMode,
      deleteNode,
      findNodeByName,
      getNewChild,
    }}>
      {children}
    </TreeContext.Provider>
  );
};

export const useTree = () => {
  const context = useContext(TreeContext);
  if (!context) throw new Error('useTree must be used within a TreeProvider');
  return context;
};


